import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useApiClient } from "@/api/client";
import {
	assetsOf,
	BrutoTarget,
	emptyMonths,
	isTargetOf,
	monthsOf,
	resolveBrutoCpm,
} from "@/modules/base_price/utils/brutoCpm";
import BasePricePlanDTO = Modules.BasePrice.Data.BasePricePlanDTO;
import BasePricePlanMinimalDTO = Modules.BasePrice.Data.BasePricePlanMinimalDTO;
import ManualCpmOverrideDTO = Modules.BasePrice.Data.Overrides.ManualCpmOverrideDTO;
import ResolvedBrutoNodeDTO = Modules.BasePrice.Data.Resolved.ResolvedBrutoNodeDTO;
import ResolvedBrutoAssetDTO = Modules.BasePrice.Data.Resolved.ResolvedBrutoAssetDTO;
import ResolvedBrutoCpmDTO = Modules.BasePrice.Data.Resolved.ResolvedBrutoCpmDTO;

const randomUuid = (): string =>
	crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

/**
 * Editor state for one bruto base price plan.
 *
 * A bruto plan is nothing but CPMs: one twelve-month MANUAL_CPM override per
 * node or screen that sets any, inherited down the tree. The tree and its
 * screens are fetched from the server once per version; after that every edit
 * only touches `draftOverrides`, and the figures on screen are re-resolved
 * locally (see resolveBrutoCpm) rather than waiting on a round trip.
 *
 * Nothing is written until Save version, which — like netto — writes a new
 * version of the chain and archives the one it came from.
 */
export const useBasePriceBrutoStore = defineStore("base-price-bruto", () => {
	const plan = ref<BasePricePlanDTO | null>(null);
	const loading = ref(false);
	const resolving = ref(false);
	const saving = ref(false);

	/** The tree and its screens as the server resolved them for the saved version. */
	const structure = ref<ResolvedBrutoNodeDTO[]>([]);
	const draftOverrides = ref<ManualCpmOverrideDTO[]>([]);

	/** Every node with the CPMs the draft puts in effect. */
	const nodes = computed<ResolvedBrutoNodeDTO[]>(() =>
		resolveBrutoCpm(structure.value, draftOverrides.value)
	);

	const nodeById = (id: number): ResolvedBrutoNodeDTO | null =>
		nodes.value.find((node) => node.hierarchy_node_id === id) ?? null;

	const childrenOf = (id: number): ResolvedBrutoNodeDTO[] =>
		nodes.value.filter((node) => node.parent_id === id);

	/** Root first, ending with the node's parent. */
	const ancestorsOf = (id: number): ResolvedBrutoNodeDTO[] => {
		const ancestors: ResolvedBrutoNodeDTO[] = [];
		let current = nodeById(id);

		while (current?.parent_id != null) {
			const parent = nodeById(current.parent_id);
			if (!parent) break;
			ancestors.unshift(parent);
			current = parent;
		}

		return ancestors;
	};

	const selectedNodeId = ref<number | null>(null);
	/** Set when a screen under the selected node is being edited instead of the node. */
	const selectedAssetId = ref<number | null>(null);

	const selectedNode = computed(() =>
		selectedNodeId.value === null ? null : nodeById(selectedNodeId.value)
	);

	const selectedAsset = computed<ResolvedBrutoAssetDTO | null>(() =>
		selectedNode.value === null || selectedAssetId.value === null
			? null
			: assetsOf(selectedNode.value).find(
					(asset) => asset.asset_id === selectedAssetId.value
			  ) ?? null
	);

	const selectedTarget = computed<BrutoTarget | null>(() =>
		selectedNodeId.value === null
			? null
			: { nodeId: selectedNodeId.value, assetId: selectedAssetId.value }
	);

	/** The figures being edited — the selected screen's, else the selected node's. */
	const selectedCpm = computed<ResolvedBrutoCpmDTO | null>(
		() => selectedAsset.value?.cpm ?? selectedNode.value?.cpm ?? null
	);

	const selectNode = (id: number) => {
		selectedNodeId.value = id;
		selectedAssetId.value = null;
	};

	const selectAsset = (nodeId: number, assetId: number) => {
		selectedNodeId.value = nodeId;
		selectedAssetId.value = assetId;
	};

	const overrideFor = (target: BrutoTarget): ManualCpmOverrideDTO | null =>
		draftOverrides.value.find((override) => isTargetOf(override, target)) ??
		null;

	/**
	 * Writes a target's twelve own months. A target left with nothing set has
	 * no reason to carry a row, so it is removed and the target inherits every
	 * month again — the same thing resetToInherited() does on purpose.
	 */
	const writeMonths = (target: BrutoTarget, months: (number | null)[]) => {
		const others = draftOverrides.value.filter(
			(override) => !isTargetOf(override, target)
		);

		if (!plan.value || months.every((month) => month === null)) {
			draftOverrides.value = others;

			return;
		}

		const existing = overrideFor(target);
		const bookUuid = Object.values(plan.value.books ?? {})[0]?.uuid;
		if (!bookUuid) return;

		draftOverrides.value = [
			...others,
			{
				uuid: existing?.uuid ?? randomUuid(),
				plan_uuid: plan.value.uuid,
				book_uuid: bookUuid,
				hierarchy_node_id: target.assetId === null ? target.nodeId : null,
				asset_id: target.assetId,
				type: "manual_cpm",
				monthly_cpm: months,
			},
		];
	};

	const ownMonths = (target: BrutoTarget): (number | null)[] => {
		const override = overrideFor(target);

		return override ? monthsOf(override.monthly_cpm) : emptyMonths();
	};

	/** Sets — and so locks — one month on the selected target, or clears it back to inherited with null. */
	const setMonth = (month: number, value: number | null) => {
		const target = selectedTarget.value;
		if (!target) return;

		const months = ownMonths(target);
		months[month] = value;
		writeMonths(target, months);
	};

	/** Sets every month of the selected target to the same CPM. */
	const setAllMonths = (value: number) => {
		const target = selectedTarget.value;
		if (!target) return;

		writeMonths(
			target,
			Array.from({ length: 12 }, () => value)
		);
	};

	/** Clears every month the selected target sets, so it inherits the whole year. */
	const resetToInherited = () => {
		const target = selectedTarget.value;
		if (!target) return;

		writeMonths(target, emptyMonths());
	};

	/** How many of the twelve months a node or screen sets itself. */
	const ownMonthCount = (target: BrutoTarget): number =>
		ownMonths(target).filter((month) => month !== null).length;

	/**
	 * What a save would write, independent of the order overrides were edited
	 * in and of the uuids handed to rows created here.
	 */
	const draftFingerprint = computed(() =>
		JSON.stringify(
			draftOverrides.value
				.map((override) =>
					JSON.stringify([
						override.hierarchy_node_id,
						override.asset_id,
						monthsOf(override.monthly_cpm),
					])
				)
				.sort()
		)
	);

	const savedFingerprint = ref("");

	/** Presents what is loaded as unsaved work — used when an archived version is reopened. */
	const markUnsaved = () => {
		savedFingerprint.value = "";
	};

	const hasUnsavedChanges = computed(
		() =>
			plan.value !== null && draftFingerprint.value !== savedFingerprint.value
	);

	const loadPlan = async (uuid: string) => {
		loading.value = true;

		const response = await useApiClient("basePrice").getPlan(uuid);
		if (response.isOk()) {
			plan.value = response.value;
			draftOverrides.value = Object.values(plan.value.overrides ?? {})
				.filter((override) => override.type === "manual_cpm")
				.map((override) => ({
					...(override as ManualCpmOverrideDTO),
					monthly_cpm: monthsOf((override as ManualCpmOverrideDTO).monthly_cpm),
				}));
			savedFingerprint.value = draftFingerprint.value;
		}

		loading.value = false;
	};

	/**
	 * Fetches the tree and its screens. The figures that come back are
	 * recomputed locally from the draft anyway, so this is only needed once per
	 * version, not after every edit.
	 */
	const loadResolvedPlan = async (uuid: string) => {
		resolving.value = true;

		const response = await useApiClient("basePrice").resolveBrutoPlan(uuid, {
			overrides: draftOverrides.value,
		});
		if (response.isOk()) {
			structure.value = Object.values(response.value.nodes ?? {});

			if (selectedNodeId.value === null) {
				const root = structure.value.find((node) => node.parent_id === null);
				selectedNodeId.value = root?.hierarchy_node_id ?? null;
			}
		}

		resolving.value = false;
	};

	/**
	 * Persists the draft as a new version. Returns the plan that was created —
	 * a different uuid to the one being edited — so the caller can move to it.
	 */
	const savePlan = async (): Promise<BasePricePlanDTO | null> => {
		if (!plan.value) return null;

		saving.value = true;

		const response = await useApiClient("basePrice").savePlan(plan.value.uuid, {
			overrides: draftOverrides.value,
		});

		saving.value = false;

		return response.isOk() ? response.value : null;
	};

	const versions = ref<BasePricePlanMinimalDTO[]>([]);
	const loadingVersions = ref(false);

	const loadVersions = async () => {
		if (!plan.value) return;

		loadingVersions.value = true;

		const response = await useApiClient("basePrice").getPlanVersions(
			plan.value.uuid
		);
		if (response.isOk()) {
			versions.value = response.value;
		}

		loadingVersions.value = false;
	};

	const $reset = () => {
		plan.value = null;
		structure.value = [];
		draftOverrides.value = [];
		savedFingerprint.value = "";
		selectedNodeId.value = null;
		selectedAssetId.value = null;
		versions.value = [];
		loadingVersions.value = false;
		loading.value = false;
		resolving.value = false;
		saving.value = false;
	};

	return {
		plan,
		loading,
		resolving,
		saving,
		nodes,
		draftOverrides,
		nodeById,
		childrenOf,
		ancestorsOf,
		selectedNodeId,
		selectedAssetId,
		selectedNode,
		selectedAsset,
		selectedTarget,
		selectedCpm,
		selectNode,
		selectAsset,
		setMonth,
		setAllMonths,
		resetToInherited,
		ownMonthCount,
		hasUnsavedChanges,
		markUnsaved,
		loadPlan,
		loadResolvedPlan,
		savePlan,
		versions,
		loadingVersions,
		loadVersions,
		$reset,
	};
});
